import { combine, forward, guard, sample } from 'effector'
import {
  $declaration,
  $defaultFilterValues,
  $disabledFilterValues,
  $filterValues,
  $internalFilterValues,
  hasCitySelection,
  mergeValues,
  propagateToURLFx,
  removeValues,
  replaceValues,
  resetValues,
  setValues
} from 'features/filter/model/units'
import { fetchDeclarationFx } from 'features/filter/model/units'
import {
  filterDeclarationToFilterValues,
  getDisabledValues,
  getFilterValuesFromUrl,
  mergeFilterValues,
  removeFilterValues,
  replaceFilterValues
} from 'features/filter/model/utils'
import { FilterDeclaration, FilterValues } from 'features/filter/types'

import { setFilterWithDefaultValue } from '../../cars-list/model'
import { $areCitiesSelected, $selectedCities } from '../../city-select/model'

$declaration.on(fetchDeclarationFx.doneData, (_, payload) => {
  if (window.sortBrands) {
    return { ...payload, sections: window.sortBrands(payload?.sections) }
  }
  return payload
})

/**
 * Изначальные значения фильтра из урл, стандартных значений
 */
sample({
  source: guard({
    source: combine({
      declaration: $declaration,
      defaultFilterValues: $defaultFilterValues.state
    }),
    filter: (
      value
    ): value is {
      declaration: FilterDeclaration
      defaultFilterValues: FilterValues
    } => Boolean(value.declaration) && Boolean(value.defaultFilterValues)
  }),
  fn: ({ declaration, defaultFilterValues }) => {
    const url = new URL(window.location.href)
    let urlHaveFilter = false
    declaration.sections.forEach((item) => {
      if (url.searchParams.get(item.key)) {
        urlHaveFilter = true
      }
    })

    if (!urlHaveFilter) {
      setFilterWithDefaultValue(true)
    }
    const baseValues = filterDeclarationToFilterValues(declaration)
    const withDefaultValues = urlHaveFilter
      ? mergeFilterValues(baseValues, {}, declaration)
      : mergeFilterValues(baseValues, defaultFilterValues, declaration)
    const withValuesFromUrl = mergeFilterValues(withDefaultValues, getFilterValuesFromUrl(declaration), declaration)
    return withValuesFromUrl
  },
  target: $internalFilterValues
})

sample({
  source: guard({
    source: {
      internalFilterValues: $internalFilterValues,
      declaration: $declaration,
      hasCitySelection: hasCitySelection.state,
      areCitiesSelected: $areCitiesSelected,
      selectedCities: $selectedCities
    },
    filter: (
      value
    ): value is {
      internalFilterValues: FilterValues
      declaration: FilterDeclaration
      hasCitySelection: { enabled: boolean }
      areCitiesSelected: boolean
      selectedCities: string[]
    } => {
      const { hasCitySelection, declaration, areCitiesSelected, internalFilterValues } = value
      const citiesAreReady = !hasCitySelection.enabled || areCitiesSelected
      return !!declaration && !!internalFilterValues && citiesAreReady
    }
  }),
  fn: ({ internalFilterValues, selectedCities, declaration }) => {
    return getDisabledValues({ ...internalFilterValues, city: selectedCities }, declaration)
  },
  target: $disabledFilterValues
})

sample({
  source: guard({
    source: {
      internalValues: $internalFilterValues,
      disabledValues: $disabledFilterValues,
      declaration: $declaration
    },
    filter: (
      value
    ): value is {
      internalValues: FilterValues
      disabledValues: FilterValues
      declaration: FilterDeclaration
    } => {
      return !!value.disabledValues && !!value.internalValues && !!value.declaration
    }
  }),
  fn: ({ internalValues, disabledValues, declaration }) => {
    const withoutDisabled = removeFilterValues(internalValues, disabledValues, declaration)
    return withoutDisabled
  },
  target: $filterValues
})

/**
 * Объединение значений фильтра
 */
sample({
  source: guard({
    source: [$declaration, $filterValues],
    filter: (source): source is [FilterDeclaration, FilterValues] => !!source[0] && !!source[1]
  }),
  clock: mergeValues,
  fn: ([declaration, filter], newValues) => mergeFilterValues(filter, newValues, declaration),
  target: $internalFilterValues
})

sample({
  source: $filterValues,
  clock: replaceValues,
  fn: replaceFilterValues,
  target: $internalFilterValues
})

/**
 * Удаление значений фильтра
 */
sample({
  source: guard({
    source: [$declaration, $filterValues],
    filter: (source): source is [FilterDeclaration, FilterValues] => !!source[0] && !!source[1]
  }),
  clock: removeValues,
  fn: ([declaration, filter], valuesToRemove) => {
    return removeFilterValues(filter, valuesToRemove, declaration)
  },
  target: $internalFilterValues
})

/**
  Сбросим фильтр когда вызывается событие ресета
  и заполним его значениями из УРЛ (если есть)
*/
sample({
  source: guard({ source: $declaration, filter: Boolean }),
  fn: filterDeclarationToFilterValues,
  clock: resetValues,
  target: setValues
})

forward({
  from: guard({ source: $filterValues, filter: Boolean }),
  to: propagateToURLFx
})
